/* pdp-gallery.js — self-contained PDP gallery + lightbox (spec Leak 1).
   No dependencies. Bound on script execution (defer), i.e. before window.load. */
(function () {
  'use strict';

  var TAP_SLOP = 10;      // px of movement that still counts as a tap
  var SWIPE_MIN = 55;     // px horizontal travel to change image in the lightbox

  function num(el, attr) { return parseInt(el.getAttribute(attr), 10) || 0; }
  function dist(a, b) { return Math.hypot(a.x - b.x, a.y - b.y); }
  function clamp(v, lo, hi) { return Math.max(lo, Math.min(hi, v)); }

  /* ------------------------------------------------------------------ */
  /*  Lightbox                                                          */
  /* ------------------------------------------------------------------ */
  function Lightbox(root, frames) {
    var dlg = root.querySelector('[data-pdp-lightbox]');
    var img = root.querySelector('[data-pdp-lb-img]');
    var pan = root.querySelector('[data-pdp-lb-pan]');
    var stage = root.querySelector('[data-pdp-lb-stage]');
    var vp = root.querySelector('[data-pdp-lb-viewport]');
    var countEl = root.querySelector('[data-pdp-lb-count]');
    var liveEl = root.querySelector('[data-pdp-lb-live]');
    var closeBtn = root.querySelector('[data-pdp-lb-close]');
    var prevBtn = root.querySelector('[data-pdp-lb-prev]');
    var nextBtn = root.querySelector('[data-pdp-lb-next]');
    var spinner = root.querySelector('[data-pdp-lb-spinner]');
    var thumbsEls = Array.prototype.slice.call(root.querySelectorAll('[data-pdp-lb-thumb]'));

    var RM = window.matchMedia('(prefers-reduced-motion: reduce)');
    var DUR_OPEN = 200, DUR_NAV = 240;    // mirror the CSS --lb-dur-* tokens
    var ZOOM_TAP = 2.5, MAX_ZOOM = 3;     // LB-ZOOM-01/03 tap target; LB-ZOOM-09 ceiling

    var items = frames.map(function (f) {
      var im = f.querySelector('img');
      var full = im.getAttribute('data-pdp-full');
      return {
        full: full,
        srcset: im.getAttribute('data-pdp-full-srcset') || '',
        zoom: im.getAttribute('data-pdp-zoom') || full,   // LB-ZOOM-06: hi-res, loaded on first zoom-in
        placeholder: im.currentSrc || im.src,
        alt: im.getAttribute('alt') || ''
      };
    });
    var hiRes = {};   // index -> decoded hi-res Image, once loaded

    var idx = 0, opener = null, spinTimer = 0, token = 0, closeTimer = 0, navTimer = 0, navTarget = 0, layerTimer = 0;
    var isOpen = false, closing = false, savedScrollY = 0, kbd = false;   // kbd: keyboard used this open session
    var scale = 1, tx = 0, ty = 0;
    var pts = {}, moved = false, startX = 0, startY = 0, baseTx = 0, baseTy = 0;
    var pinchBase = 0, pinchScale0 = 1, lastTap = 0, downTarget = null;
    var axis = null, dragDismiss = false, lastY = 0, lastT = 0, vy = 0;

    // Explicit lifecycle state — PDP-IMAGE-LIGHTBOX-001 §4:
    // closed | opening | idle | navigating | zoomed | panning | dismissing.
    // Mirrored to [data-lb-state] for CSS / analytics hooks. isOpen/closing are
    // kept as-is; state is additive in this step (consumers land in step 2).
    var state = 'closed';
    function setState(s) { state = s; dlg.dataset.lbState = s; }
    setState('closed');

    /* ---- transform layers ------------------------------------------- */
    // LB-MOTION-04: promote the animated layers only while something is moving;
    // auto-drop them ~0.5s after the last activity (covers every tween + gesture).
    function armLayers() {
      stage.style.willChange = 'transform';
      pan.style.willChange = 'transform';
      clearTimeout(layerTimer);
      layerTimer = setTimeout(disarmLayers, 500);
    }
    function disarmLayers() {
      clearTimeout(layerTimer);
      stage.style.willChange = 'auto';
      pan.style.willChange = 'auto';
    }
    function applyZoom() {
      pan.style.transform = 'translate(' + tx + 'px,' + ty + 'px) scale(' + scale + ')';
      var zoomed = scale > 1.05;
      pan.classList.toggle('is-zoomed', zoomed);
      // reflect zoom state only when otherwise at rest — never clobber
      // opening / navigating / panning / dismissing
      if (state === 'idle' || state === 'zoomed') setState(zoomed ? 'zoomed' : 'idle');
    }
    function resetZoom() {                 // tweened (pan keeps its CSS transition)
      scale = 1; tx = 0; ty = 0;
      pan.classList.remove('is-tracking');
      applyZoom();
    }
    // LB-ZOOM-06: on the first zoom-in of an image, fetch its hi-res source and
    // swap it in once decoded — no flash, no empty frame (the scaled fit image
    // stays visible until then). Re-shows a cached hi-res immediately.
    function loadHiRes(i) {
      var it = items[i];
      if (!it.zoom || it.zoom === it.full) return;
      if (hiRes[i]) {
        if (i === idx && img.src !== hiRes[i].src) {
          img.removeAttribute('srcset');
          img.src = hiRes[i].currentSrc || it.zoom;
        }
        return;
      }
      var probe = new Image();
      var mine = token;
      var swapped = false;
      var done = function () {
        if (swapped) { hiRes[i] = probe; return; }
        swapped = true;
        hiRes[i] = probe;
        if (mine !== token || i !== idx) return;   // navigated away — keep cached, don't swap
        img.removeAttribute('srcset');
        img.src = probe.currentSrc || it.zoom;
      };
      probe.onload = done;                          // bytes ready — guaranteed fallback
      probe.src = it.zoom;
      if (probe.decode) probe.decode().then(done).catch(function () { /* onload covers it */ });
    }
    function zoomTo(s, focusX, focusY, continuous) {
      s = clamp(s, 1, MAX_ZOOM);
      if (!continuous) pan.classList.remove('is-tracking');   // discrete zoom -> allow the tween
      armLayers();
      if (s <= 1.02) { resetZoom(); return; }
      loadHiRes(idx);
      var r = img.getBoundingClientRect();
      var cx = (focusX == null ? 0 : focusX - (r.left + r.width / 2));
      var cy = (focusY == null ? 0 : focusY - (r.top + r.height / 2));
      var prev = scale;
      scale = s;
      tx = tx - cx * (scale - prev) / prev;
      ty = ty - cy * (scale - prev) / prev;
      clampPan();
    }
    function clampPan() {
      var r = img.getBoundingClientRect();
      var maxX = Math.max(0, (r.width - vp.clientWidth) / 2 + 1);
      var maxY = Math.max(0, (r.height - vp.clientHeight) / 2 + 1);
      tx = clamp(tx, -maxX, maxX);
      ty = clamp(ty, -maxY, maxY);
      applyZoom();
    }

    /* ---- image loading (blur-up + spinner + ±1 preload) ------------- */
    function preload(i) {
      if (i < 0 || i >= items.length) return;
      var im = new Image();
      im.sizes = '100vw';
      if (items[i].srcset) im.srcset = items[i].srcset;
      im.src = items[i].full;
    }

    function render() {
      resetZoom();
      var ri = idx;
      var it = items[ri];
      var mine = ++token;
      if (countEl) countEl.textContent = (ri + 1) + ' / ' + items.length;
      if (liveEl) liveEl.textContent = 'Photo ' + (ri + 1) + ' of ' + items.length;
      var atStart = ri <= 0, atEnd = ri >= items.length - 1;
      prevBtn.disabled = atStart;
      nextBtn.disabled = atEnd;
      prevBtn.setAttribute('aria-disabled', atStart);   // LB-NAV-05: disabled + aria-disabled
      nextBtn.setAttribute('aria-disabled', atEnd);
      img.alt = it.alt;

      for (var ti = 0; ti < thumbsEls.length; ti++) {   // LB-NAV-04: mark + reveal active thumb
        if (ti === ri) thumbsEls[ti].setAttribute('aria-current', 'true');
        else thumbsEls[ti].removeAttribute('aria-current');
      }
      if (thumbsEls[ri]) {
        thumbsEls[ri].scrollIntoView({ inline: 'center', block: 'nearest', behavior: RM.matches ? 'auto' : 'smooth' });
      }

      var loader = new Image();
      loader.sizes = '100vw';
      if (it.srcset) loader.srcset = it.srcset;
      loader.src = it.full;

      // LB-NAV-06: if the full image is already in cache (e.g. the ±1 preload
      // from a prior render), swap straight to it — no placeholder frame, no
      // blur-up, no spinner. This is what removes the flash on rapid nav.
      if (loader.complete && loader.naturalWidth) {
        clearTimeout(spinTimer);
        spinner.hidden = true;
        img.removeAttribute('data-loading');
        img.sizes = '100vw';
        if (it.srcset) img.srcset = it.srcset; else img.removeAttribute('srcset');
        img.src = loader.currentSrc || it.full;
        preload(ri + 1);
        preload(ri - 1);
        return;
      }

      // not cached — blur-up placeholder + delayed spinner
      img.setAttribute('data-loading', '');
      img.removeAttribute('srcset');
      img.src = it.placeholder;

      clearTimeout(spinTimer);
      spinner.hidden = true;
      spinTimer = setTimeout(function () { if (mine === token) spinner.hidden = false; }, 120);

      var failTimer = setTimeout(clearBlur, 8000);
      function clearBlur() {
        if (mine !== token) return;
        clearTimeout(spinTimer);
        clearTimeout(failTimer);
        img.removeAttribute('data-loading');
        spinner.hidden = true;
      }
      loader.onload = function () {
        if (mine !== token) return;
        img.sizes = '100vw';
        if (it.srcset) img.srcset = it.srcset;
        img.src = loader.currentSrc || it.full;
        clearBlur();
      };
      loader.onerror = clearBlur;
      if (loader.complete && loader.naturalWidth) loader.onload();

      preload(ri + 1);
      preload(ri - 1);
    }

    /* ---- navigation (LB-NAV-06: slide + fade, one transition at a time;
       rapid presses fold into navTarget and resolve to the final image) --- */
    function navSettle() {
      if (!isOpen || closing) return;
      if (idx !== navTarget) { stepNav(navTarget > idx ? 1 : -1); return; }
      setState(scale > 1.05 ? 'zoomed' : 'idle');
    }
    function stepNav(dir) {
      idx = navTarget;
      render();                                  // token discards any stale load
      setState('navigating');
      armLayers();
      clearTimeout(navTimer);
      navTimer = setTimeout(navSettle, RM.matches ? 0 : DUR_NAV + 20);
      if (RM.matches) { img.style.opacity = '1'; img.style.transform = 'none'; return; }
      img.style.transition = 'none';
      img.style.opacity = '0';
      img.style.transform = 'translateX(' + (dir > 0 ? 28 : -28) + 'px)';
      void img.offsetWidth;                               // flush the start state
      img.style.transition = '';                          // back to the CSS transition
      img.style.opacity = '1';
      img.style.transform = 'translateX(0)';
    }
    function goTo(n) {                               // absolute nav (thumbnails)
      if (closing || !isOpen) return;
      n = clamp(n, 0, items.length - 1);
      if (state === 'navigating') { navTarget = n; return; }   // fold into the in-flight move
      if (n === idx) return;
      navTarget = n;
      stepNav(n > idx ? 1 : -1);
    }
    function go(dir) {                               // relative nav (arrows / swipe / keys)
      var base = state === 'navigating' ? navTarget : idx;
      goTo(base + dir);
    }

    /* ---- open / close (LB-OPEN-06a / LB-DISMISS-06) ---------------- */
    function open(i, fromEl, viaKeyboard) {
      // LB-A11Y-02/07: the focus trap + background inertness come from
      // showModal(). No non-modal fallback — unsupported engines fall through to
      // the <a href> full-image link (see the trigger markup).
      if (typeof dlg.showModal !== 'function') return;
      clearTimeout(closeTimer);
      closing = false;
      kbd = !!viaKeyboard;
      setState('opening');
      idx = clamp(i || 0, 0, items.length - 1);
      navTarget = idx;
      opener = fromEl || null;
      render();
      stage.style.transform = '';
      dlg.style.opacity = '';
      dlg.classList.remove('is-open');
      if (!dlg.open) {
        // LB-OPEN-07: pin the page — overflow:hidden alone doesn't hold on iOS
        savedScrollY = window.scrollY || window.pageYOffset || 0;
        document.body.style.top = -savedScrollY + 'px';
        dlg.showModal();
      }
      document.documentElement.classList.add('pdp-lb-open');
      armLayers();
      // flush the opacity:0 / scale(.96) start state, then transition from it
      void dlg.offsetWidth;
      dlg.classList.add('is-open');
      isOpen = true;
      // focus the dialog container (tabindex="-1"), not the close button — a
      // pointer-open must not paint a :focus-visible ring on a control. SR still
      // announces the dialog's aria-label; Tab from here lands on Close.
      try { dlg.focus({ preventScroll: true }); } catch (e) { dlg.focus(); }
      setTimeout(function () {
        if (isOpen && !closing && state === 'opening') setState('idle');
      }, DUR_OPEN + 20);
    }
    function finishClose() {
      if (!closing) return;                 // run once (transitionend + timer both call)
      clearTimeout(closeTimer);
      clearTimeout(navTimer);
      if (dlg.open) dlg.close();
      document.documentElement.classList.remove('pdp-lb-open');
      document.body.style.top = '';                       // LB-DISMISS-07: unpin
      stage.style.transform = '';
      stage.style.transition = '';
      disarmLayers();
      dlg.style.opacity = '';
      resetZoom();
      isOpen = false; closing = false;
      setState('closed');
      // Return focus to the trigger without scrolling, THEN restore the saved
      // position last (dlg.close() / a plain focus() would scroll it into view).
      // Only paint the trigger's focus ring if keyboard was actually used —
      // a pointer close must not leave a gold outline on the hero image.
      if (opener) {
        try { opener.focus({ preventScroll: true, focusVisible: kbd }); }
        catch (e) { try { opener.focus({ preventScroll: true }); } catch (e2) {} }
      }
      window.scrollTo(0, savedScrollY);                   // LB-DISMISS-07: restore scroll
    }
    function close(viaDrag) {
      if (closing) return;
      closing = true;
      setState('dismissing');
      dlg.classList.remove('is-open');
      if (RM.matches) { finishClose(); return; }
      armLayers();
      stage.style.transition = 'transform ' + DUR_OPEN + 'ms var(--lb-ease, ease)';
      stage.style.transform = viaDrag
        ? 'translateY(' + Math.round(vp.clientHeight * 0.5) + 'px) scale(.92)'
        : 'scale(.96)';
      closeTimer = setTimeout(finishClose, DUR_OPEN + 60);
      dlg.addEventListener('transitionend', function te(ev) {
        if (ev.target === dlg && ev.propertyName === 'opacity') {
          dlg.removeEventListener('transitionend', te);
          finishClose();
        }
      });
    }

    /* ---- wiring --------------------------------------------------- */
    closeBtn.addEventListener('click', function () { close(); });
    prevBtn.addEventListener('click', function () { go(-1); });
    nextBtn.addEventListener('click', function () { go(1); });
    thumbsEls.forEach(function (th) {
      th.addEventListener('click', function () { goTo(num(th, 'data-pdp-lb-thumb')); });
    });
    dlg.addEventListener('cancel', function (e) { e.preventDefault(); close(); });
    dlg.addEventListener('contextmenu', function (e) { e.preventDefault(); });
    img.addEventListener('dragstart', function (e) { e.preventDefault(); });
    dlg.addEventListener('keydown', function (e) {
      kbd = true;   // any key in the dialog -> restore the trigger's focus ring on close
      // LB-DISMISS-02: close on Esc explicitly — don't depend on the native
      // <dialog> `cancel` event alone (it does not fire in every engine / the
      // non-showModal fallback path). close() is guarded against double-close.
      if (e.key === 'Escape') { e.preventDefault(); close(); return; }
      if (e.key === 'ArrowRight') { e.preventDefault(); go(1); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); go(-1); }
      else if (e.key === 'Home') { e.preventDefault(); go(-items.length); }
      else if (e.key === 'End') { e.preventDefault(); go(items.length); }
      else if (e.key === '+' || e.key === '=') { e.preventDefault(); zoomTo(scale + 0.6); }
      else if (e.key === '-' || e.key === '_') { e.preventDefault(); zoomTo(scale - 0.6); }
    });

    vp.addEventListener('pointerdown', function (e) {
      pts[e.pointerId] = { x: e.clientX, y: e.clientY };
      moved = false; axis = null; dragDismiss = false;
      armLayers();                                     // promote layers for the gesture
      downTarget = e.target;
      startX = e.clientX; startY = e.clientY; baseTx = tx; baseTy = ty;
      lastY = e.clientY; lastT = e.timeStamp || Date.now(); vy = 0;
      var k = Object.keys(pts);
      if (k.length === 2) {
        pinchBase = dist(pts[k[0]], pts[k[1]]);
        pinchScale0 = scale;
        pan.classList.add('is-tracking');
      }
      if (vp.setPointerCapture) { try { vp.setPointerCapture(e.pointerId); } catch (x) {} }
    });

    vp.addEventListener('pointermove', function (e) {
      if (!pts[e.pointerId]) return;
      pts[e.pointerId] = { x: e.clientX, y: e.clientY };
      var k = Object.keys(pts);

      if (k.length === 2 && pinchBase) {                 // pinch = continuous, 1:1
        pan.classList.add('is-tracking');
        var mx = (pts[k[0]].x + pts[k[1]].x) / 2;        // anchor the zoom at the fingers (LB-ZOOM-01)
        var my = (pts[k[0]].y + pts[k[1]].y) / 2;
        zoomTo(pinchScale0 * (dist(pts[k[0]], pts[k[1]]) / pinchBase), mx, my, true);
        moved = true;
        return;
      }

      var dx = e.clientX - startX, dy = e.clientY - startY;
      var now = e.timeStamp || Date.now();
      if (now !== lastT) { vy = (e.clientY - lastY) / (now - lastT); lastY = e.clientY; lastT = now; }
      if (!moved && (Math.abs(dx) > TAP_SLOP || Math.abs(dy) > TAP_SLOP)) moved = true;

      if (scale > 1.05) {                                // zoomed -> pan, 1:1
        pan.classList.add('is-tracking');
        armLayers();
        if (state === 'zoomed' || state === 'idle') setState('panning');
        tx = baseTx + dx; ty = baseTy + dy;
        applyZoom();
        return;
      }

      // fit scale: lock the axis on the first real move
      if (axis === null && moved) axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';

      if (axis === 'y' && dy > 0) {                      // LB-DISMISS-04: drag down to dismiss
        dragDismiss = true;
        armLayers();
        stage.classList.add('is-dragging');
        var k2 = clamp(dy / 1800, 0, 0.12);
        stage.style.transform = 'translateY(' + dy + 'px) scale(' + (1 - k2) + ')';
        dlg.style.opacity = String(clamp(1 - dy / (vp.clientHeight * 0.5), 0, 1));
        if (e.cancelable) e.preventDefault();
      }
      // axis === 'x' resolves as swipe-nav on pointerup
    });

    function endPointer(e) {
      var multi = Object.keys(pts).length > 1;
      delete pts[e.pointerId];
      if (Object.keys(pts).length > 0) return;

      var dx = e.clientX - startX, dy = e.clientY - startY;
      // LB-DISMISS-03: dismiss only on a tap clearly outside the *visible* photo
      // plus a grace margin. The <img> element box fills the viewport, so derive
      // the letterboxed rect from object-fit:contain — a near-miss on the photo
      // edge must not close the lightbox and lose the shopper's place.
      var GRACE = 24;
      var b = img.getBoundingClientRect();
      var nw = img.naturalWidth || b.width, nh = img.naturalHeight || b.height;
      var fit = Math.min(b.width / nw, b.height / nh) || 1;
      var pw = nw * fit, ph = nh * fit;
      var pl = b.left + (b.width - pw) / 2, pt = b.top + (b.height - ph) / 2;
      var onImage = e.clientX >= pl - GRACE && e.clientX <= pl + pw + GRACE &&
                    e.clientY >= pt - GRACE && e.clientY <= pt + ph + GRACE;

      // pinch just ended: snap to fit if we're basically un-zoomed (LB-ZOOM-02)
      if (pinchBase) {
        pinchBase = 0;
        pan.classList.remove('is-tracking');
        if (scale < 1.05) resetZoom(); else clampPan();
        setState(scale > 1.05 ? 'zoomed' : 'idle');
        return;
      }

      if (dragDismiss) {                                 // release the drag-to-dismiss
        stage.classList.remove('is-dragging');
        var past = dy > vp.clientHeight * 0.12;
        var flick = dy > 48 && vy > 0.55;
        if (past || flick) { close(true); }
        else {
          stage.style.transition = 'transform ' + DUR_OPEN + 'ms var(--lb-ease, ease)';
          stage.style.transform = '';
          dlg.style.opacity = '';
          setTimeout(function () { stage.style.transition = ''; }, DUR_OPEN + 40);
        }
        return;
      }

      // finished a pan drag on a zoomed image -> settle bounds
      if (scale > 1.05 && moved) { pan.classList.remove('is-tracking'); clampPan(); setState('zoomed'); return; }

      if (!moved && !multi && onImage) {                 // tap / click on the image
        var now = Date.now();
        var dbl = now - lastTap < 300;
        lastTap = now;
        if (scale > 1.05) { resetZoom(); lastTap = 0; return; }   // any input: tap a zoomed image -> fit
        if (e.pointerType === 'touch') {
          if (dbl) zoomTo(ZOOM_TAP, e.clientX, e.clientY);        // LB-ZOOM-01: double-tap toggles zoom
          return;                                                 // single tap at fit on touch is inert
        }
        zoomTo(ZOOM_TAP, e.clientX, e.clientY);                   // LB-ZOOM-03: pointer click zooms at the cursor
        return;
      }
      if (!moved && !multi && !onImage) { close(); return; }   // backdrop tap

      if (axis === 'x' && Math.abs(dx) > SWIPE_MIN) go(dx < 0 ? 1 : -1);   // swipe-nav
    }
    vp.addEventListener('pointerup', endPointer);
    vp.addEventListener('pointercancel', endPointer);
    vp.addEventListener('dblclick', function (e) { e.preventDefault(); });
    vp.addEventListener('wheel', function (e) {
      if (!dlg.open) return;
      e.preventDefault();
      pan.classList.remove('is-tracking');
      zoomTo(scale + (e.deltaY < 0 ? 0.4 : -0.4), e.clientX, e.clientY);
    }, { passive: false });

    return { open: open };
  }

  /* ------------------------------------------------------------------ */
  /*  Inline gallery                                                     */
  /* ------------------------------------------------------------------ */
  function initGallery(root) {
    if (root.__pdpInit) return;
    root.__pdpInit = true;

    var track = root.querySelector('[data-pdp-track]');
    if (!track) return;
    var slides = Array.prototype.slice.call(track.children);
    var frames = Array.prototype.slice.call(root.querySelectorAll('[data-pdp-open]'));
    var prevBtn = root.querySelector('[data-pdp-prev]');
    var nextBtn = root.querySelector('[data-pdp-next]');
    var countEl = root.querySelector('[data-pdp-count]');
    var dots = Array.prototype.slice.call(root.querySelectorAll('[data-pdp-dot]'));
    var thumbs = Array.prototype.slice.call(root.querySelectorAll('[data-pdp-thumb]'));
    var hint = root.querySelector('[data-pdp-hint]');
    var totalImg = frames.length;

    function slideIndex() {
      var w = track.clientWidth || 1;
      return clamp(Math.round(track.scrollLeft / w), 0, slides.length - 1);
    }
    function scrollToSlide(s) {
      s = clamp(s, 0, slides.length - 1);
      // Instant, not smooth: scroll-snap (mandatory + snap-stop:always) cancels
      // programmatic smooth scrolls in Chromium, and instant keeps the transition
      // uninterruptible-by-design (spec Leak 1: honour every tap).
      // Use the slide's real offset (not s * clientWidth) so it lands exactly on
      // the boundary — otherwise a sub-pixel gap shows a sliver of the neighbour.
      var el = slides[s];
      track.scrollTo({ left: el ? el.offsetLeft : s * track.clientWidth, behavior: 'auto' });
    }
    function imageIndexToSlide(k) {
      for (var s = 0; s < slides.length; s++) {
        var f = slides[s].querySelector('[data-pdp-open]');
        if (f && num(f, 'data-pdp-open') === k) return s;
      }
      return k;
    }
    function currentImageIndex() {
      var f = slides[slideIndex()] ? slides[slideIndex()].querySelector('[data-pdp-open]') : null;
      return f ? num(f, 'data-pdp-open') : clamp(slideIndex(), 0, totalImg - 1);
    }

    function sync() {
      var s = slideIndex();
      var k = currentImageIndex();
      if (countEl) countEl.textContent = 'Image ' + (k + 1) + ' of ' + totalImg;
      dots.forEach(function (d, di) {
        if (di === k) d.setAttribute('aria-current', 'true'); else d.removeAttribute('aria-current');
      });
      thumbs.forEach(function (th, ti) {
        if (ti === k) th.setAttribute('aria-current', 'true'); else th.removeAttribute('aria-current');
      });
      if (prevBtn) prevBtn.hidden = s <= 0;
      if (nextBtn) nextBtn.hidden = s >= slides.length - 1;
    }

    // Position tracking: IntersectionObserver is the source of truth (fires
    // reliably when a slide becomes majority-visible, independent of scroll-event
    // timing); scroll/scrollend/resize are backup nudges.
    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        var best = null;
        entries.forEach(function (en) {
          if (en.isIntersecting && (!best || en.intersectionRatio > best.intersectionRatio)) best = en;
        });
        if (best && best.intersectionRatio >= 0.55) sync();
      }, { root: track, threshold: [0.55, 0.9] });
      slides.forEach(function (s) { io.observe(s); });
    }
    var st = 0;
    track.addEventListener('scroll', function () {
      clearTimeout(st);
      st = setTimeout(sync, 60);
    }, { passive: true });
    track.addEventListener('scrollend', sync);
    window.addEventListener('resize', sync);

    // Belt over unreliable scroll events: after any touch/drag on the track,
    // rAF-poll scrollLeft until it settles (covers momentum fling) and keep the
    // counter/dots in sync the whole time.
    function pollUntilSettled() {
      var last = NaN, stable = 0;
      (function step() {
        var sl = track.scrollLeft;
        sync();
        if (sl === last) { if (++stable > 6) return; } else { stable = 0; last = sl; }
        requestAnimationFrame(step);
      })();
    }
    track.addEventListener('pointerup', pollUntilSettled, { passive: true });
    track.addEventListener('pointercancel', pollUntilSettled, { passive: true });
    track.addEventListener('touchend', pollUntilSettled, { passive: true });

    function afterNav() { dropHint(); sync(); setTimeout(sync, 90); }

    // interruptible: each click reads the live scroll position, no lock
    if (nextBtn) nextBtn.addEventListener('click', function () { scrollToSlide(slideIndex() + 1); afterNav(); });
    if (prevBtn) prevBtn.addEventListener('click', function () { scrollToSlide(slideIndex() - 1); afterNav(); });
    dots.forEach(function (d) {
      d.addEventListener('click', function () { scrollToSlide(imageIndexToSlide(num(d, 'data-pdp-dot'))); afterNav(); });
    });
    thumbs.forEach(function (th) {
      th.addEventListener('click', function () { scrollToSlide(imageIndexToSlide(num(th, 'data-pdp-thumb'))); afterNav(); });
    });

    function dropHint() { if (hint) hint.setAttribute('data-hidden', ''); }
    // dismiss on the first real touch anywhere on the gallery (no timer — the
    // page can take 7-40s to load on mobile ad traffic, so a load-time timer
    // would hide the hint before the user can act on it)
    root.addEventListener('pointerdown', dropHint, { passive: true, once: true });
    // no long-press "Save image…" sheet on the inline images either
    root.addEventListener('contextmenu', function (e) {
      if (e.target && e.target.closest('.pdp-gallery__frame')) e.preventDefault();
    });

    var lb = Lightbox(root, frames);
    frames.forEach(function (f) {
      f.addEventListener('click', function (e) {
        e.preventDefault();                       // trigger is an <a> (no-JS full-image fallback)
        lb.open(num(f, 'data-pdp-open'), f, e.detail === 0);   // detail 0 = keyboard-activated
        dropHint();
      });
    });

    root.__pdpReady = true;
    root.setAttribute('data-ready', '');
    sync();

    // LB-ANTI-02: replay a tap captured by the inline shim before this ran
    var pending = root.__pdpPending;
    root.__pdpPending = null;
    if (pending && Date.now() - pending.ts < 10000) {
      lb.open(pending.i, frames[pending.i] || null);
      dropHint();
    }
  }

  function boot() {
    var list = document.querySelectorAll('[data-pdp-gallery]');
    for (var i = 0; i < list.length; i++) initGallery(list[i]);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  // re-init after a colour soft-swap replaces the product section
  document.addEventListener('pdp:swapped', boot);
})();
